import React, { useEffect, useState } from "react";
import { Shield, UserPlus, Mail, Loader2, Trash2, Users, Layers,
          Check, X, Send } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * AdminModule — rendered as a full-screen modal triggered from the
 * dashboard's "Admin" button. Visible only when `user.role` is `admin`
 * or `super_admin`.
 *
 * Capabilities:
 *  • Browse all users in scope (super_admin → all; admin → invited by self).
 *  • Invite a new user (name, role, project access, email, password).
 *  • Activate / re-send credentials email.
 *  • Delete a user (cannot delete self or other super-admins).
 */
export default function AdminModule({ allProjects = [], onUsersChange,
                                       onClose }) {
  const { user } = useAuth();
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState("");
  const [msg, setMsg]         = useState("");
  const [showInvite, setShowInvite] = useState(false);

  const role = (user?.role || "").toLowerCase();
  const isAdmin = role === "admin" || role === "super_admin";

  const load = async () => {
    if (!isAdmin) return;
    setLoading(true); setErr("");
    try {
      const r = await api.get("/admin/users");
      setUsers(r.data?.users || []);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  if (!isAdmin) return null;

  return (
    <div data-testid="admin-module-modal"
          style={{ position: "fixed", inset: 0, zIndex: 1000,
                    background: "rgba(0,0,0,0.55)",
                    display: "flex", alignItems: "flex-start",
                    justifyContent: "center",
                    padding: "5vh 12px", overflowY: "auto" }}>
      <div className="pf-card"
            style={{ width: "min(1100px, 100%)", padding: 0,
                      maxHeight: "90vh", display: "flex",
                      flexDirection: "column" }}>
        {/* Header */}
        <div style={{ padding: "16px 20px",
                       borderBottom: "1px solid var(--pf-border)",
                       display: "flex", alignItems: "center", gap: 12 }}>
          <Shield size={20} color="var(--pf-spark)"/>
          <div style={{ flex: 1 }}>
            <h2 className="pf-heading"
                 style={{ fontSize: 20, margin: 0 }}>Admin Module</h2>
            <div style={{ fontSize: 12, color: "var(--pf-text-muted)" }}>
              {role === "super_admin"
                ? "Super-Admin · all users on the platform"
                : "Admin · users you invited"} ·
              <span style={{ marginLeft: 6 }}>
                <Users size={11} style={{ verticalAlign: -1 }}/> {users.length}
              </span>
            </div>
          </div>
          <button className="btn btn-primary"
                  onClick={() => setShowInvite(true)}
                  data-testid="admin-invite-btn"
                  style={{ display: "inline-flex", alignItems: "center",
                            gap: 6 }}>
            <UserPlus size={14}/> Add user
          </button>
          <button className="btn btn-ghost" onClick={onClose}
                  data-testid="admin-module-close"
                  style={{ padding: "6px 10px" }}>
            <X size={14}/>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>
          {err && <div className="pf-sev pf-sev-error"
                        data-testid="admin-error"
                        style={{ display: "block", padding: "8px 10px",
                                  marginBottom: 10 }}>{err}</div>}
          {msg && <div className="pf-sev pf-sev-info"
                        data-testid="admin-msg"
                        style={{ display: "block", padding: "8px 10px",
                                  marginBottom: 10 }}>{msg}</div>}
          {loading && (
            <div style={{ fontSize: 12, color: "var(--pf-text-muted)",
                            marginBottom: 8 }}>
              <Loader2 size={12} className="pf-spin"/> Loading users…
            </div>
          )}

          <table style={{ width: "100%", fontSize: 12,
                            borderCollapse: "collapse" }}
                  data-testid="admin-users-table">
            <thead>
              <tr style={{ background: "var(--pf-surface-2)",
                            color: "var(--pf-text-muted)",
                            textAlign: "left" }}>
                <th style={th}>Name</th>
                <th style={th}>Email</th>
                <th style={th}>Role</th>
                <th style={th}>Verified</th>
                <th style={th}>Must Change PW</th>
                <th style={th}>Projects</th>
                <th style={th}>Created</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <UserRow key={u.id} u={u}
                          isSelf={u.id === user.id}
                          onChanged={() => { load(); onUsersChange && onUsersChange(); }}
                          setMsg={setMsg}/>
              ))}
              {users.length === 0 && !loading && (
                <tr><td colSpan={8} style={{ ...td, color: "var(--pf-text-muted)",
                                                fontStyle: "italic",
                                                textAlign: "center" }}>
                  No users yet. Click "Add user" to invite one.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showInvite && (
        <InviteUserModal allProjects={allProjects}
                          onClose={() => setShowInvite(false)}
                          onCreated={() => { setShowInvite(false); load(); }}/>
      )}
    </div>
  );
}


const th = { padding: "6px 8px", borderBottom: "1px solid var(--pf-border)",
              fontWeight: 600, fontSize: 11, textTransform: "uppercase",
              letterSpacing: "0.04em" };
const td = { padding: "8px", borderBottom: "1px solid var(--pf-border)" };


function UserRow({ u, isSelf, onChanged, setMsg }) {
  const [busy, setBusy] = useState(false);
  const [actBusy, setActBusy] = useState(false);

  const remove = async () => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${u.id}`);
      onChanged();
    } catch (e) {
      alert(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const activate = async () => {
    if (!window.confirm(`Send a fresh activation email to ${u.email}? `
        + `Their existing password will be replaced with a new temporary one.`)) return;
    setActBusy(true); setMsg && setMsg("");
    try {
      const r = await api.post(`/admin/users/${u.id}/activate`);
      setMsg && setMsg(r.data?.message || `Activation email sent to ${u.email}.`);
      onChanged();
    } catch (e) {
      alert(formatErr(e.response?.data?.detail) || e.message);
    } finally { setActBusy(false); }
  };

  return (
    <tr data-testid={`admin-user-row-${u.id}`}>
      <td style={td}>{u.name || "—"}</td>
      <td style={td}><code>{u.email}</code></td>
      <td style={td}>
        <span style={{ padding: "2px 6px", borderRadius: 3, fontSize: 10,
                        background: u.role === "super_admin" ? "#7a1f1f"
                          : u.role === "admin" ? "#1e4e8c" : "#3a3f4a",
                        color: "#fff" }}>
          {u.role}
        </span>
      </td>
      <td style={td}>{u.email_verified ? <Check size={12} color="#22c55e"/>
        : <X size={12} color="#dc2626"/>}</td>
      <td style={td}>{u.must_change_password ? "Yes" : "No"}</td>
      <td style={td}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Layers size={11}/> {u.project_count}
        </span>
      </td>
      <td style={td}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
      <td style={td}>
        <div style={{ display: "inline-flex", gap: 6 }}>
          <button onClick={activate} disabled={actBusy}
                  data-testid={`admin-user-activate-${u.id}`}
                  title="Send activation email with fresh credentials"
                  className="btn btn-ghost"
                  style={{ padding: "2px 8px", fontSize: 11,
                            color: "var(--pf-spark)" }}>
            {actBusy ? <Loader2 size={11} className="pf-spin"/>
              : <Send size={11}/>}
          </button>
          {!isSelf && u.role !== "super_admin" && (
            <button onClick={remove} disabled={busy}
                    data-testid={`admin-user-delete-${u.id}`}
                    title="Delete this user"
                    className="btn btn-ghost"
                    style={{ padding: "2px 8px", fontSize: 11,
                              color: "var(--pf-err)" }}>
              {busy ? <Loader2 size={11} className="pf-spin"/>
                : <Trash2 size={11}/>}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}


function InviteUserModal({ allProjects, onClose, onCreated }) {
  const [name, setName]         = useState("");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState(_genTempPassword());
  const [roleVal, setRoleVal]   = useState("user");
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState("");

  const togglePick = (pid) => {
    setSelectedProjects(cur => cur.includes(pid)
      ? cur.filter(p => p !== pid)
      : [...cur, pid]);
  };
  const ok = name.trim() && email.trim() && password.length >= 8;
  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/admin/users", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        role: roleVal,
        password,
        project_ids: selectedProjects,
      });
      onCreated();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-invite-modal"
          style={{ position: "fixed", inset: 0,
                    background: "rgba(0, 0, 0, 0.55)",
                    display: "flex", alignItems: "center",
                    justifyContent: "center", zIndex: 1100 }}>
      <div className="pf-card"
            style={{ width: 540, maxHeight: "92vh", overflowY: "auto",
                      padding: 24 }}>
        <h2 className="pf-heading" style={{ fontSize: 20, margin: "0 0 4px" }}>
          Invite a new user
        </h2>
        <p style={{ color: "var(--pf-text-muted)", fontSize: 12,
                     margin: "0 0 18px" }}>
          The user will receive an email with their credentials and will
          be forced to change the password on first login.
        </p>

        <label className="pf-label">FULL NAME</label>
        <input className="pf-input" value={name}
                onChange={e => setName(e.target.value)}
                data-testid="admin-invite-name"/>
        <div style={{ height: 10 }}/>

        <label className="pf-label">EMAIL ID</label>
        <input className="pf-input" type="email" value={email}
                onChange={e => setEmail(e.target.value)}
                data-testid="admin-invite-email"/>
        <div style={{ height: 10 }}/>

        <label className="pf-label">ROLE</label>
        <select className="pf-input" value={roleVal}
                onChange={e => setRoleVal(e.target.value)}
                data-testid="admin-invite-role">
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </select>
        <div style={{ height: 10 }}/>

        <label className="pf-label">TEMPORARY PASSWORD
          <span style={{ color: "var(--pf-text-muted)", marginLeft: 6,
                          fontWeight: 400 }}>
            (auto-generated · editable · user must change on first login)
          </span>
        </label>
        <div style={{ display: "flex", gap: 8 }}>
          <input className="pf-input" value={password}
                  onChange={e => setPassword(e.target.value)}
                  data-testid="admin-invite-password"
                  style={{ flex: 1 }}/>
          <button type="button" className="btn btn-ghost"
                  onClick={() => setPassword(_genTempPassword())}
                  data-testid="admin-invite-regenerate"
                  style={{ padding: "0 12px" }}>
            Regenerate
          </button>
        </div>
        <div style={{ height: 14 }}/>

        <label className="pf-label">
          PROJECT ACCESS
          <span style={{ color: "var(--pf-text-muted)", marginLeft: 6,
                          fontWeight: 400 }}>
            (multi-select · user will see only what you assign)
          </span>
        </label>
        <div data-testid="admin-invite-projects"
              style={{ maxHeight: 180, overflowY: "auto",
                        background: "var(--pf-surface-2)",
                        border: "1px solid var(--pf-border)",
                        padding: 8, borderRadius: 4 }}>
          {allProjects.length === 0 && (
            <div style={{ fontSize: 11, color: "var(--pf-text-muted)",
                           fontStyle: "italic" }}>
              No projects to assign yet — create one first.
            </div>
          )}
          {allProjects.map(p => (
            <label key={p.id}
                    data-testid={`admin-invite-project-${p.id}`}
                    style={{ display: "flex", alignItems: "center",
                              gap: 6, padding: "3px 0", fontSize: 12,
                              cursor: "pointer" }}>
              <input type="checkbox"
                      checked={selectedProjects.includes(p.id)}
                      onChange={() => togglePick(p.id)}/>
              <span style={{ flex: 1 }}>{p.name}</span>
              <span style={{ color: "var(--pf-text-muted)",
                              fontSize: 10 }}>
                {p.domain}{p.sub_type ? " · " + p.sub_type : ""}
              </span>
            </label>
          ))}
        </div>

        {err && <div className="pf-sev pf-sev-error"
                      data-testid="admin-invite-error"
                      style={{ display: "block", padding: "8px 10px",
                                marginTop: 12 }}>{err}</div>}

        <div style={{ display: "flex", gap: 8, marginTop: 18,
                       justifyContent: "flex-end" }}>
          <button onClick={onClose} className="btn btn-ghost"
                  data-testid="admin-invite-cancel">Cancel</button>
          <button onClick={submit} disabled={!ok || busy}
                  className="btn btn-primary"
                  data-testid="admin-invite-submit">
            {busy ? <><Loader2 size={12} className="pf-spin"/> Sending…</>
              : <><Mail size={12}/> Create &amp; send invitation</>}
          </button>
        </div>
      </div>
    </div>
  );
}


function _genTempPassword() {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghjkmnpqrstuvwxyz";
  const digit = "23456789";
  const spec  = "!@#$%^&*";
  const all   = upper + lower + digit + spec;
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  let out = pick(upper) + pick(lower) + pick(digit) + pick(spec);
  for (let i = 0; i < 8; i++) out += pick(all);
  return out.split("").sort(() => Math.random() - 0.5).join("");
}
